
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { BuyingLists, BuyingListsFilter } from '@/collections/buying_lists';
import { BuyingListModel } from '@/models/buying_list';
import { SupplierModel } from '@/models/supplier';
import { StoreLocationModel } from '@/models/store_location';
import { StoreLocationList } from '@/collections/store_locations';
import { SupplierList } from '@/collections/suppliers';
import FeatureLockedNotify from '@/components/FeatureLockedNotify.vue';
import { StoreModel } from '@/models/store';

@Component({
  components: {
    FeatureLockedNotify
  }
})
export default class BuyingListsView extends Vue {
  private buyingLists: BuyingListModel[] = [];
  private loading = false;
  private totalItems = 0;
  private search = '';
  private suppliers: SupplierModel[] = [];
  private locations: StoreLocationModel[] = [];
  private loadingSuppliers = false;
  private loadingLocations = false;

  private filters = {
    status: null as string | null,
    supplierId: null as number | null,
    locationId: null as number | null
  };

  private options = {
    page: 1,
    itemsPerPage: 10,
    sortBy: ['createdAt'],
    sortDesc: [true]
  };

  private headers = [
    { text: 'Name', value: 'name' },
    { text: 'Supplier', value: 'supplierName' },
    { text: 'Location', value: 'locationName' },
    { text: 'Status', value: 'status' },
    { text: 'Total Value', value: 'totalValue' },
    { text: 'Created', value: 'createdAt' },
    { text: 'Updated', value: 'updatedAt' },
    { text: 'Actions', value: 'actions', sortable: false }
  ];

  private statusOptions = [
    { text: 'Active', value: 'active' },
    { text: 'Finalized', value: 'finalized' },
    { text: 'Completed', value: 'completed' },
    { text: 'Canceled', value: 'canceled' },
  ];

  private showSupplierDialog = false;
  private selectedSupplierId: number | null = null;

  @Prop({ default: () => new StoreModel() })
  private store!: StoreModel;

  private get canManageBuyingLists(): boolean {
    const validateValues = [
      {
        code: 'buying_list',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  async created() {
    this.fetchSuppliers(50);
    this.fetchLocations();
    await this.fetchData();
  }

  @Watch('options', { deep: true })
  @Watch('filters', { deep: true })
  private async onFiltersChange() {
    // Check if any filters are active
    const hasActiveFilters = 
      this.filters.status !== null || 
      this.filters.supplierId !== null || 
      this.filters.locationId !== null;

    if (hasActiveFilters) {
      await this.fetchData();
    }
  }

  private async fetchData() {
    this.loading = true;
    try {
      const buyingLists = new BuyingLists();
      const params: BuyingListsFilter = {
        page: this.options.page,
        limit: this.options.itemsPerPage
      };

      if (this.filters.status) {
        params.status = this.filters.status;
      }

      if (this.filters.supplierId) {
        params.supplierId = this.filters.supplierId;
      }

      if (this.filters.locationId) {
        params.locationId = this.filters.locationId;
      }

      if (this.search) {
        params.search = this.search;
      }

      await buyingLists.fetch(params);
      this.buyingLists = buyingLists.items;
      this.totalItems = buyingLists.total;
      this.loading = false;
    } catch (error) {
      this.$emit('error', 'Failed to load buying lists');
    } finally {
      this.loading = false;
    }
  }

  private getSupplierName(supplierId: number): string {
    const supplier = this.suppliers.find(supplier => supplier.id === supplierId);
    return supplier ? supplier.name : '';
  }

  private getLocationName(locationId: number): string {
    const location = this.locations.find(location => location.id === locationId);
    return location ? location.name : '-';
  }

  @Watch('search')
  private onSearchChange() {
    // Debounce search input
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.fetchData();
    }, 300);
  }

  private searchTimeout: any = null;

  private async fetchSuppliers(itemsPerPage: number) {
    this.loadingSuppliers = true;
    try {
      const suppliers = new SupplierList();
      await suppliers.fetch();
      this.suppliers = suppliers.items;
      
      // If there are no suppliers, show a message
      if (this.suppliers.length === 0) {
        this.$emit('warning', 'No suppliers found. Please add a supplier first.');
      }
    } catch (error) {
      this.$emit('error', 'Failed to load suppliers');
      this.closeSupplierDialog();
    } finally {
      this.loadingSuppliers = false;
    }
  }

  private async fetchLocations() {
    this.loadingLocations = true;
    try {
      const locations = new StoreLocationList();
      await locations.fetch();
      this.locations = locations.items;
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      this.loadingLocations = false;
    }
  }

  private getStatusColor(status: string): string {
    switch (status) {
      case 'active': return 'amber'; // In progress/active state
      case 'finalized': return 'blue'; // Ready for review/approval
      case 'completed': return 'green'; // Successfully completed
      case 'canceled': return 'red'; // Canceled/terminated
      default: return 'grey'; // Unknown/undefined status
    }
  }

  private formatNumber(value: number): string {
    // return value?.toFixed(2) || '0.00';
    return value.toString();

  }

  private async convertToPO(item: BuyingListModel) {
    this.$router.push(`/purchase-orders/create?from_list=${item.id}`);
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.filters.status ||
      this.filters.supplierId ||
      this.filters.locationId ||
      this.search
    );
  }

  private resetFilter(filterName: string) {
    if (filterName === 'search') {
      this.search = '';
    } else {
      this.filters[filterName as keyof typeof this.filters] = null;
    }
    this.fetchData();
  }

  private resetAllFilters() {
    this.filters = {
      status: null,
      supplierId: null,
      locationId: null
    };
    this.search = '';
    this.fetchData();
  }

  private createList() {
    if (this.selectedSupplierId) {
      this.$router.push(`/suppliers/${this.selectedSupplierId}/buying-lists/create`);
      this.showSupplierDialog = false;
      this.selectedSupplierId = null;
    }
  }

  private closeSupplierDialog(): void {
    this.showSupplierDialog = false;
    this.selectedSupplierId = null;
  }

  private customFilter(item: string, queryText: string, itemText: string): boolean {
    const text = itemText.toLowerCase();
    const searchText = queryText.toLowerCase();
    return text.indexOf(searchText) > -1;
  }
}
